import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useScrollLock } from '../../../hooks/useScrollLock';

const DeleteNodeModal = ({ isOpen, node, onClose, onConfirm, isLoading }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !node) return null;

  const hasChildren = node.children?.length > 0;

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-lg shadow-elevation-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Trash2" size={20} className="text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {t('menuSettings.deleteTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">"{node.name}"</span>
              {hasChildren && (
                <span className="block mt-1 text-destructive/80">
                  {t('menuSettings.deleteWarningChildren', { count: node.children.length })}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 justify-end">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onConfirm(node.id)}
            loading={isLoading}
            disabled={isLoading}
            iconName="Trash2"
            iconPosition="left"
          >
            {t('menuSettings.deleteConfirmBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteNodeModal;
